import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Auth Store
export const useAuthStore = create(
  persist(
    (set) => ({
      token: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,
      setAuth: (token, refreshToken, user) => set({ token, refreshToken, user, isAuthenticated: true }),
      logout: () => {
        try { localStorage.removeItem('finance-auth'); } catch (e) { console.warn('Failed to clear auth from localStorage:', e.message); }
        set({ token: null, refreshToken: null, user: null, isAuthenticated: false });
      },
      updateToken: (token, refreshToken) => set({ token, refreshToken }),
      updateSelectedEntity: (entity) => set((state) => ({
        user: { ...state.user, selected_entity: entity }
      })),
    }),
    { name: 'finance-auth' }
  )
);

// Pseudo-entity constants (not real db.entities, not in EntityType enum)
// Includes group pseudo-entities for jurisdiction/scope-level filtering.
export const PSEUDO_ENTITIES = ['all', 'group:personal', 'group:france', 'group:uk', 'group:other'];

// Valid top-level scopes. 'overall' is the group-wide aggregate (all four
// other scopes combined) and the default landing after login.
export const SCOPES = ['overall', 'portfolio', 'br_os', 'entities', 'personal'];

// Valid person views within the Personal scope (single source of truth
// for keys and display labels). PERSONAL_VIEWS is derived for validation.
export const PERSONAL_VIEW_OPTIONS = [
  { key: 'fan_piotr', label: 'Personal (Fan & Piotr)', shortLabel: 'Fan & Piotr' },
];
export const PERSONAL_VIEWS = PERSONAL_VIEW_OPTIONS.map((v) => v.key);

// Entity Store
export const useEntityStore = create(
  persist(
    (set) => ({
      entities: [],
      selectedEntity: 'all', // Default to aggregate view
      selectedScope: 'overall', // Default scope: group-wide Overall dashboard
      personalView: 'fan_piotr', // Default person view in Personal scope
      setEntities: (entities) => set({ entities }),
      setSelectedEntity: (entity) => set({ selectedEntity: entity }),
      setSelectedScope: (scope) => set({ selectedScope: scope }),
      setPersonalView: (view) => set({ personalView: view }),
    }),
    {
      name: 'finance-entity',
      onRehydrateStorage: () => (state) => {
        if (!state) return;

        // Migrate persisted property_portfolio pseudo-entity to scope model
        if (state.selectedEntity === 'property_portfolio') {
          state.selectedScope = 'portfolio';
          state.selectedEntity = 'all';
        }

        // Validate scope
        if (!SCOPES.includes(state.selectedScope)) {
          state.selectedScope = 'overall';
        }

        // Validate personalView
        if (!PERSONAL_VIEWS.includes(state.personalView)) {
          state.personalView = 'fan_piotr';
        }

        const sel = state.selectedEntity;
        // Allow pseudo-entities unconditionally
        if (PSEUDO_ENTITIES.includes(sel)) return;
        // For real entity UUIDs, we cannot validate at rehydration time
        // (entities array may not be populated yet). Accept any non-empty
        // string that looks like a plausible UUID or type code.
        if (typeof sel === 'string' && sel.length > 0 && sel.length < 100) return;
        // Unknown or corrupt value: reset to default
        state.selectedEntity = 'all';
      },
    }
  )
);

// Theme Store
export const useThemeStore = create(
  persist(
    (set) => ({
      theme: 'system', // 'light', 'dark', 'system'
      setTheme: (theme) => set({ theme }),
    }),
    { name: 'finance-theme' }
  )
);

// Sidebar Store
export const useSidebarStore = create(
  persist(
    (set) => ({
      isCollapsed: false,
      toggleSidebar: () => set((state) => ({ isCollapsed: !state.isCollapsed })),
      setCollapsed: (collapsed) => set({ isCollapsed: collapsed }),
    }),
    { name: 'finance-sidebar' }
  )
);

// Portfolio Preferences Store (display currency, persisted across pages)
export const usePortfolioPrefsStore = create(
  persist(
    (set) => ({
      displayCurrency: 'GBP', // 'GBP' or 'EUR'
      setDisplayCurrency: (currency) => set({ displayCurrency: currency }),
      // 2026-07-20 managed-property gross-revenue restatement: one-time
      // dismissible banner flag. Not reset when new restatements happen in
      // the future -- if another restatement ships, use a new key.
      restatementBannerDismissed: false,
      dismissRestatementBanner: () => set({ restatementBannerDismissed: true }),
    }),
    { name: 'finance-portfolio-prefs' }
  )
);

// Alerts Store
export const useAlertsStore = create((set) => ({
  alerts: [],
  unreadCount: 0,
  setAlerts: (alerts) => set({ alerts, unreadCount: alerts.filter(a => !a.is_read).length }),
  markRead: (alertId) => set((state) => ({
    alerts: state.alerts.map(a => a.id === alertId ? { ...a, is_read: true } : a),
    unreadCount: state.unreadCount - 1
  })),
  markAllRead: () => set((state) => ({
    alerts: state.alerts.map(a => ({ ...a, is_read: true })),
    unreadCount: 0
  })),
}));
